import { useCallback } from 'react';
import { QueryClient, useQuery, useQueryClient } from '@tanstack/react-query';
import { ChartHandle } from '../chart-handle';

// The user's decision about the current unsaved preview, expressed in chat ("save it", "make a new
// one", "discard"). Matches the agent's `promote_chart` tool intent enum verbatim.
export const PromotionIntent = {
  UPDATE: 'update',
  SAVE_AS_NEW: 'save_as_new',
  DISCARD: 'discard'
} as const;
export type PromotionIntentType = (typeof PromotionIntent)[keyof typeof PromotionIntent];

export const isPromotionIntent = (value: unknown): value is PromotionIntentType =>
  value === PromotionIntent.UPDATE ||
  value === PromotionIntent.SAVE_AS_NEW ||
  value === PromotionIntent.DISCARD;

// A one-shot "the user asked, via chat, to promote this preview" note, carried in the react-query
// cache from its writer (AgentDrawer, which sees the agent's promote_chart tool call) to its consumer
// (SingleChartView, which owns the actual exit handlers). Keeping it a shared-cache signal — rather
// than having AgentDrawer save directly — means chat-driven promotion runs the SAME handleUpdate /
// handleSaveAsNew / handleDiscard the buttons do, so the fan-out confirm gates a chat "save it"
// identically. Keyed by handle (like the working copy) so it scopes to the focused chart. No
// sessionStorage sidecar: it's a transient intent, not durable state.
const pendingPromotionKey = (company: string, handle: ChartHandle) => [
  'pending-promotion',
  company,
  handle
];

export interface PendingPromotion {
  pendingPromotion: PromotionIntentType | null;
  setPendingPromotion: (intent: PromotionIntentType) => void;
  clearPendingPromotion: () => void;
}

export const usePendingPromotion = (
  company: string,
  handle: ChartHandle | null
): PendingPromotion => {
  const queryClient = useQueryClient();
  const key = handle ? pendingPromotionKey(company, handle) : ['pending-promotion', company, null];

  const { data: pendingPromotion = null } = useQuery<PromotionIntentType | null>({
    queryKey: key,
    queryFn: () => queryClient.getQueryData<PromotionIntentType>(key) ?? null,
    enabled: Boolean(company && handle),
    staleTime: Infinity,
    gcTime: Infinity
  });

  const setPendingPromotion = useCallback(
    (intent: PromotionIntentType) => {
      if (handle) {
        queryClient.setQueryData(pendingPromotionKey(company, handle), intent);
      }
    },
    [queryClient, company, handle]
  );

  const clearPendingPromotion = useCallback(() => {
    if (handle) {
      queryClient.setQueryData(pendingPromotionKey(company, handle), null);
    }
  }, [queryClient, company, handle]);

  return { pendingPromotion, setPendingPromotion, clearPendingPromotion };
};

// Set a pending promotion for a handle whose view isn't mounted yet — used when AgentDrawer mints a
// brand-new `draft:<uuid>` and the same turn asks to keep it ("make a new chart of X and save it"):
// the note is stashed on the draft handle so the draft view consumes it once it mounts.
export const seedPendingPromotion = (
  queryClient: QueryClient,
  company: string,
  handle: ChartHandle,
  intent: PromotionIntentType
): void => {
  queryClient.setQueryData(pendingPromotionKey(company, handle), intent);
};
